import { forwardRef } from 'react';
import { clsx } from 'clsx';

const Input = forwardRef(({
  label,
  helperText,
  error,
  icon: Icon,
  id,
  className,
  required,
  disabled,
  ...props
}, ref) => {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && (
        <div className="flex items-center gap-0.5">
          <label
            htmlFor={inputId}
            className={clsx(
              'text-sm font-medium',
              disabled ? 'text-text-disabled' : 'text-text-secondary',
            )}
          >
            {label}
          </label>
          {required && <span className="text-danger text-sm leading-none" aria-hidden="true">*</span>}
        </div>
      )}

      <div className="relative">
        {Icon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" aria-hidden="true">
            <Icon size={16} />
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          disabled={disabled}
          required={required}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
          className={clsx(
            'w-full rounded-md border px-3.5 py-2.5 text-base bg-surface text-text-primary',
            'placeholder:text-text-muted',
            'transition-colors duration-normal',
            'focus:outline-none focus:ring-2 focus:ring-border-focus focus:border-border-focus',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-bg-secondary',
            error
              ? 'border-danger focus:ring-danger'
              : 'border-border hover:border-border-strong',
            Icon && 'pl-9',
            className,
          )}
          {...props}
        />
      </div>

      {error && (
        <p id={`${inputId}-error`} role="alert" className="text-xs text-danger-text flex items-center gap-1">
          {error}
        </p>
      )}
      {!error && helperText && (
        <p id={`${inputId}-helper`} className="text-xs text-text-muted">
          {helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
